import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  RotateCcw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { HeroChip } from '@/components/ui/HeroChip';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import {
  ADMIN_ALWAYS,
  ADMIN_ONLY,
  ALL_PERMISSIONS,
  ROLE_KINDS,
  ROLE_KIND_HINT,
  ROLE_KIND_LABEL,
  ROLE_KIND_PERMISSIONS,
  type Permission,
  type RoleKind,
  permissionGroupsFor,
  permissionGroupTitle,
} from '@/constants/permissions';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/utils';
import { instituteApi } from '../../api/institute.api';
import { FeatureAccessRow } from '../components/FeatureAccessRow';
import { RoleAccessMeter, coverageOf } from '../components/RoleAccessMeter';
import { roleSchema, type RoleFormValues, type RolePayload } from '../schemas/role.schema';
import { QueryError } from '@/components/ui/QueryError';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';

const ROLES_PATH = '/admin/roles';
// No kind: a new role is asked who it is for before anything can be ticked.
const EMPTY_FORM: Partial<RoleFormValues> = { name: '', description: '', permissions: [] };

const KIND_OPTIONS = ROLE_KINDS.map((kind) => ({ value: kind, label: ROLE_KIND_LABEL[kind] }));

/**
 * Creating or editing one role, on its own page.
 *
 * It used to be a dialog, on the argument that a role is "a name and a list of
 * ticks". That list is thirty-eight ticks across ten areas of the product, and
 * deciding what a job may do is the most consequential thing an administrator
 * does on any screen — a scrolling box over a dimmed page is the wrong room for
 * it. A page gives it the width to show the areas side by side, a live picture
 * of what is being granted, and an address somebody can be sent to.
 *
 * The same page serves both jobs; `uuid` in the path is the only difference,
 * exactly as the class setup screen does it.
 */
export function RoleSetupPage() {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const isEdit = Boolean(uuid);

  const permissions = useAuthStore((state) => state.user?.permissions);
  // The pages this school has, and what the catalogue calls them — the groups
  // below are named and filtered by these, as the sidebar is.
  const schoolFeatures = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const canManage = Boolean(permissions?.includes('ROLE_MANAGE'));

  const [formError, setFormError] = useState<string | null>(null);
  const [featureSearch, setFeatureSearch] = useState('');
  const [clearedPermissions, setClearedPermissions] = useState<Permission[] | null>(null);

  /**
   * The roles list, only to find the one being edited.
   *
   * There is no endpoint for a single role, and adding one for a screen that
   * always arrives from the list would be a route to maintain for no reader.
   */
  const {
    data: roles,
    isLoading,
    error,
    refetch,
  } = useApiQuery(() => (isEdit ? instituteApi.listRoles() : Promise.resolve([])), [isEdit]);

  const role = useMemo(
    () => (isEdit ? (roles?.find((item) => item.uuid === uuid) ?? null) : null),
    [isEdit, roles, uuid],
  );

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: EMPTY_FORM,
  });
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = form;
  useLiveFormErrors(form);

  // Filled once the role arrives; a create never waits for anything.
  useEffect(() => {
    if (!role) return;
    reset({
      name: role.name,
      description: role.description ?? '',
      kind: role.kind,
      permissions: role.permissions,
    });
  }, [role, reset]);

  const held = watch('permissions') ?? [];
  const kind = watch('kind') as RoleKind | undefined;
  /**
   * What this role may be given. The Administrator role is not limited by its
   * kind; any other role is offered only its kind's keys, which the API also
   * enforces (`ROLE_KIND_PERMISSIONS`).
   */
  const grantableKeys: readonly Permission[] = role?.isSystem
    ? ALL_PERMISSIONS
    : kind
      ? ROLE_KIND_PERMISSIONS[kind]
      : [];
  /** Fixed on the Administrator role, and while anybody holds the role — the API refuses both. */
  const kindLockedReason = role?.isSystem
    ? 'The Administrator role is always the school office.'
    : role && role.userCount > 0
      ? 'Cannot change while people hold this role. Create a new role instead.'
      : null;
  const coverage = coverageOf(held, schoolFeatures, featureNames);

  // Matched on the sidebar name, the one an administrator already knows the
  // page by. Filtering only hides rows — what hidden rows grant is untouched.
  const featureQuery = featureSearch.trim().toLowerCase();
  const featureGroups = permissionGroupsFor(schoolFeatures)
    .map((group) => ({ group, title: permissionGroupTitle(group, featureNames) }))
    .filter(({ title }) => !featureQuery || title.toLowerCase().includes(featureQuery));

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    // Spelled out because the resolver types its output as the form's input:
    // an emptied description must go as null, which JSON keeps, so an edit
    // clears it rather than leaving the old one in place.
    const payload: RolePayload = { ...values, description: values.description || null };

    try {
      const saved = isEdit
        ? await instituteApi.updateRole(uuid as string, payload)
        : await instituteApi.createRole(payload);

      toast.success(
        isEdit ? `${saved.name} has been updated` : `${saved.name} is ready to use`,
        `${saved.permissions.length} of ${ALL_PERMISSIONS.length} permissions granted.`,
      );
      navigate(ROLES_PATH);
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        applyFieldErrors(setError, caught);
        setFormError(caught.message);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setFormError('Something went wrong. Please try again.');
    }
  });

  const shell = cn('flex flex-col gap-6 animate-hero-zoom', CONSOLE_PALETTE);

  if (error) {
    return (
      <div className={shell}>
        <QueryError title="Could not load this role" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (isEdit && isLoading) {
    return (
      <div className={shell}>
        <Skeleton className="h-44 w-full rounded-3xl" />
        <Skeleton className="h-96 w-full rounded-3xl" />
      </div>
    );
  }

  if (isEdit && !role) {
    return (
      <div className={shell}>
        <Alert tone="critical" title="That role could not be found">
          It may have been deleted.{' '}
          <Link to={ROLES_PATH} className="font-[560] underline">
            Back to roles
          </Link>
        </Alert>
      </div>
    );
  }

  return (
    <div className={shell}>
      {/* Top Header with Breadcrumb navigation, title, and Reach progress bar */}
      <div className="flex flex-col gap-4 sm:gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0 flex-1">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm font-medium mb-1.5">
            <Link
              to={ROLES_PATH}
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 hover:underline transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Roles</span>
            </Link>
            <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
            <span className="text-slate-900 dark:text-white font-bold">
              {isEdit ? 'Edit role' : 'Add role'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {isEdit ? `Edit ${role?.name ?? 'role'}` : 'New role'}
          </h1>
          <p className="mt-1 max-w-xl text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">
            A role is a job, and the keys that job needs. Tick only what somebody doing it could not work without.
          </p>

          {(role?.isSystem || (isEdit && role && role.userCount > 0)) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {role?.isSystem && (
                <HeroChip icon={ShieldCheck}>Came with your institute</HeroChip>
              )}
              {isEdit && role && role.userCount > 0 && (
                <HeroChip icon={Sparkles}>
                  {role.userCount} {role.userCount === 1 ? 'person holds it' : 'people hold it'}
                </HeroChip>
              )}
            </div>
          )}
        </div>

        {/* Right side: Reach Progress Bar */}
        <div className="w-full lg:w-[380px] shrink-0 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-3.5 sm:p-4 shadow-sm transition-all">
          <RoleAccessMeter
            coverage={coverage}
            held={held.length}
            total={ALL_PERMISSIONS.length}
          />
          <p className="mt-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            {held.length === 0
              ? 'Nothing granted yet — this role would open no part of the product.'
              : `Reaches ${coverage.filter((group) => group.held > 0).length} of ${coverage.length} areas.`}
          </p>
        </div>
      </div>

      {!canManage && (
        <Alert tone="info" title="Read only">
          Your role lets you see how access is arranged but not change it. Ask your institute
          administrator if something needs adjusting.
        </Alert>
      )}

      {formError && (
        <Alert tone="critical" title="Could not save this role">
          {formError}
        </Alert>
      )}

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        <fieldset disabled={!canManage || isSubmitting} className="contents">
          {/* --- what it is called ------------------------------------- */}
          <section className="flex flex-col gap-4 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] sm:p-5 animate-slide-down delay-75">
            <div className="flex items-center gap-3.5">
              <span
                className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25"
                aria-hidden
              >
                <Sparkles className="relative h-5 w-5" />
                <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate text-base font-black tracking-tight text-slate-900 dark:text-white">
                  The job
                </h2>
                <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                  Name it after the job somebody does, not after the access it carries.
                </p>
              </div>
            </div>

            <span
              aria-hidden
              className="block h-px w-full bg-gradient-to-r from-blue-300 via-blue-200 to-transparent dark:from-blue-700 dark:via-blue-900 dark:to-transparent"
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Role name"
                placeholder="Class teacher, Accounts clerk, Front office"
                autoComplete="off"
                error={errors.name?.message}
                {...register('name')}
              />
              <Textarea
                label="What this role is for (optional)"
                placeholder="Takes the register for one section and enters its marks."
                rows={1}
                error={errors.description?.message}
                {...register('description')}
              />
            </div>

            <div className="w-full sm:max-w-sm">
              <Select
                label="Who is this role for?"
                placeholder="Choose the kind of staff"
                options={KIND_OPTIONS}
                disabled={Boolean(kindLockedReason)}
                error={errors.kind?.message}
                hint={kindLockedReason ?? (kind ? ROLE_KIND_HINT[kind] : 'Decides which pages this role can be given.')}
                {...register('kind', {
                  // Ticks the new kind cannot hold are taken off, so the role is
                  // never left holding a page it could not be saved with.
                  onChange: (event: { target: { value: string } }) => {
                    const allowed = ROLE_KIND_PERMISSIONS[event.target.value as RoleKind] ?? [];
                    setValue(
                      'permissions',
                      (getValues('permissions') ?? []).filter((key) => allowed.includes(key)),
                      { shouldDirty: true },
                    );
                  },
                })}
              />
            </div>
          </section>

          {/* --- the keys ---------------------------------------------- */}
          <Controller
            name="permissions"
            control={control}
            render={({ field }) => {
              const chosen = new Set(field.value ?? []);

              function set(next: Set<Permission>) {
                if (role?.isSystem) for (const permission of ADMIN_ALWAYS) next.add(permission);
                field.onChange([...next]);
              }

              /** Swaps one feature's keys for `granted`, leaving every other feature alone. */
              function setGroup(groupPermissions: readonly Permission[], granted: Permission[]) {
                const next = new Set(chosen);
                for (const permission of groupPermissions) next.delete(permission);
                for (const permission of granted) next.add(permission);
                set(next);
                setClearedPermissions(null);
              }

              function handleClearAll() {
                setClearedPermissions([...chosen]);
                set(new Set());
              }

              function handleUndo() {
                if (clearedPermissions && clearedPermissions.length > 0) {
                  set(new Set(clearedPermissions));
                  setClearedPermissions(null);
                }
              }

              return (
                <section className="flex flex-col gap-4 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-4 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)] sm:p-5 animate-slide-down delay-100">
                  <div className="flex flex-wrap items-center gap-3.5">
                    <span
                      className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25"
                      aria-hidden
                    >
                      <KeyRound className="relative h-5 w-5" />
                      <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-base font-black tracking-tight text-slate-900 dark:text-white">
                        What it allows
                      </h2>
                      <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                        Tick each feature this job uses, then choose view only or full access.
                      </p>
                    </div>
                    {chosen.size > (role?.isSystem ? ADMIN_ALWAYS.length : 0) ? (
                      <button
                        type="button"
                        onClick={handleClearAll}
                        className="shrink-0 cursor-pointer rounded-full border border-rose-200 dark:border-rose-900/60 bg-rose-50/50 dark:bg-rose-950/30 px-3 py-1.5 text-[11.5px] font-bold text-rose-700 dark:text-rose-300 transition-colors hover:border-rose-300 dark:hover:border-rose-800 hover:bg-rose-100/60 dark:hover:bg-rose-900/40 hover:text-rose-800 dark:hover:text-rose-200"
                      >
                        Clear all
                      </button>
                    ) : clearedPermissions && clearedPermissions.length > 0 ? (
                      <button
                        type="button"
                        onClick={handleUndo}
                        className="inline-flex items-center gap-1.5 shrink-0 cursor-pointer rounded-full border border-indigo-300 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-950/30 px-3 py-1.5 text-[11.5px] font-bold text-indigo-600 dark:text-indigo-400 transition-colors hover:border-indigo-400 dark:hover:border-indigo-600 hover:bg-indigo-100/70 dark:hover:bg-indigo-900/50 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        <RotateCcw className="h-3 w-3" />
                        Undo
                      </button>
                    ) : null}
                  </div>

                  {errors.permissions?.message && (
                    <Alert tone="critical" title="Choose what this role allows">
                      {errors.permissions.message}
                    </Alert>
                  )}

                  <div className="relative w-full sm:max-w-md lg:max-w-lg">
                    <Input
                      label="Search sidebar items"
                      hideLabel
                      type="text"
                      placeholder="Search sidebar items…"
                      autoComplete="off"
                      leftIcon={<Search className="h-3.5 w-3.5 text-slate-400" />}
                      value={featureSearch}
                      onChange={(event) => setFeatureSearch(event.target.value)}
                      className="!h-10 !py-2 !pl-9.5 !pr-8 !text-[13px] !font-medium !text-slate-800 dark:!text-slate-100 !rounded-xl !bg-white dark:!bg-[#111c2e] !border-slate-300 dark:!border-slate-600 hover:!border-slate-400 dark:hover:!border-slate-500 focus:!border-indigo-600 dark:focus:!border-indigo-400 focus:!ring-0 focus:!outline-none !outline-none shadow-xs transition-colors placeholder:text-slate-400 dark:placeholder:text-slate-500 w-full"
                    />
                    {featureSearch && (
                      <button
                        type="button"
                        onClick={() => setFeatureSearch('')}
                        aria-label="Clear search"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>

                  {/* One row per page this school has, named as the sidebar
                      names it: tick to give it, then choose how much. */}
                  {!role?.isSystem && !kind ? (
                    <Alert tone="info" title="Choose who this role is for first">
                      Pick the kind of staff above, and the pages this role can be given
                      appear here.
                    </Alert>
                  ) : featureGroups.length === 0 ? (
                    <p
                      role="status"
                      className="py-6 text-center text-sm font-medium text-slate-500 dark:text-slate-400"
                    >
                      No sidebar item matches “{featureSearch.trim()}”.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                      {featureGroups.map(({ group, title }) => {
                        const grantable = group.permissions.filter((permission) =>
                          grantableKeys.includes(permission),
                        );
                        // Staff and Roles go to the Administrator role alone; the
                        // rest follow the role's kind. The API refuses both.
                        const unavailableReason =
                          grantable.length > 0
                            ? undefined
                            : group.permissions.every((permission) => ADMIN_ONLY.includes(permission))
                              ? 'Administrator only'
                              : `Not for ${kind ? ROLE_KIND_LABEL[kind].toLowerCase() : 'this'} roles`;

                        return (
                          <FeatureAccessRow
                            key={group.key}
                            title={title}
                            permissions={group.permissions}
                            chosen={chosen}
                            locked={role?.isSystem ? ADMIN_ALWAYS : undefined}
                            grantable={grantable}
                            unavailableReason={unavailableReason}
                            onChange={(granted) => setGroup(group.permissions, granted)}
                          />
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            }}
          />
        </fieldset>

        {/* The save bar rides the bottom: ten panels of checkboxes run well past
            a screen, and a button that has scrolled away is one nobody presses. */}
        {canManage && (
          <div className="sticky bottom-4 z-10">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white/90 dark:bg-[#111c2e]/90 px-4 py-3 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.45)] backdrop-blur-xl">
              <p className="flex min-w-0 items-center gap-2.5 text-[12.5px] font-bold text-slate-600 dark:text-slate-300">
                {held.length > 0 ? (
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400"
                    aria-hidden
                  />
                ) : (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-amber animate-glow" aria-hidden />
                )}
                {held.length === 0
                  ? 'Choose at least one thing this role allows.'
                  : `${held.length} of ${ALL_PERMISSIONS.length} permissions granted${
                      isEdit && !isDirty ? ' · nothing changed yet' : ''
                    }.`}
              </p>

              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                <Button type="button" variant="ghost" onClick={() => navigate(ROLES_PATH)} className="w-full sm:w-auto justify-center">
                  Cancel
                </Button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-xl bg-emerald-600 tracking-wide hover:bg-emerald-700 active:bg-emerald-800 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-600/30 active:scale-[0.98] inline-flex cursor-pointer items-center justify-center gap-2 transition-all disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 w-full sm:w-auto"
                >
                  {isSubmitting ? (
                    <span>Saving…</span>
                  ) : (
                    <>
                      <Save className="h-4 w-4 stroke-[2.5]" aria-hidden />
                      <span>{isEdit ? 'Save changes' : 'Create role'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
